<template>
  <div>
    <slot name="label" />
    <ui-button
      v-model:is-menu-open="isMenuOpen"
      class="ui-period-picker"
      icon="fas fa-calendar-alt"
      :text="text"
      :disabled="disabled"
      :has-menu="true"
    >
      <template #menu>
        <div class="ui-period-picker__menu">
          <div class="ui-period-picker__menu-content">
            <div class="ui-period-picker__menu-picker-wrapper">
              <ui-date-picker
                v-model="dateValue"
                mode="single"
                :min-date="minDate"
                :max-date="maxDate"
                @input="onChange"
              />
            </div>

            <slot name="append" />
          </div>

          <div class="ui-period-picker__menu-footer">
            <div class="ui-period-picker__menu-range">
              <ui-chip class="ml-4 ml-first-0 hidden-sm-and-down">{{ dateFormatted }}</ui-chip>
              <ui-select
                v-model="time"
                item-text="value"
                item-value="value"
                class="picker-select"
                :items="availableTimes"
                :hide-details="1"
                :autocomplete="false"
                @change="onChange"
              />
            </div>

            <div class="ui-period-picker__menu-range-actions">
              <slot name="actions">
                <ui-button
                  class="ml-8"
                  icon="fas fa-times"
                  :text="t('Close')"
                  @click="toggleMenu"
                />
              </slot>
            </div>
          </div>
        </div>
      </template>
    </ui-button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import moment, { type Moment } from 'moment';
import { useI18n } from '@/composables';

const getTimeFormatted = (num: number) => (num < 10 ? `0${num}` : num);

const TIMES = Array(25)
  .fill(null)
  .map((_, index) => `${getTimeFormatted(index)}:00`);

const splitTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const offset = +minutes <= 30 ? 0 : 1;
  return +hours + offset;
};

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    value?: string;
    withTimeFilter?: boolean;
    disabled?: boolean;
    format?: string;
    minDate?: string | Date | null;
    maxDate?: string | Date | null;
  }>(),
  {
    withTimeFilter: false,
    format: 'YYYY-MM-DD HH:mm:ss',
    minDate: null,
    maxDate: null,
  },
);

const emit = defineEmits<{
  input: [value: Moment];
}>();

const { t } = useI18n();

const externalValue = computed(() =>
  props.modelValue !== undefined ? props.modelValue : props.value,
);

const isMenuOpen = ref(false);

const date = ref(new Date(externalValue.value));
const time = ref(
  `${getTimeFormatted(splitTime(moment(externalValue.value).format('HH:mm')))}:00` || TIMES[0],
);

const dateValue = computed({
  get: () => date.value,
  set: (value: Date | null) => {
    date.value = value || new Date();
  },
});

const times = computed(() => TIMES.map((item) => ({ value: item })));

const isSameDay = (now: Date, today = moment()) => moment(today).isSame(now, 'day');

const availableTimes = computed(() => {
  if (props.withTimeFilter && isSameDay(date.value)) {
    const now = +moment().format('HH');
    return times.value.map(({ value: timeValue }) => {
      const hour = +timeValue.split(':')[0];
      return {
        value: timeValue,
        disabled: hour < now + 1,
      };
    });
  }
  return times.value;
});

const dateFormatted = computed(() => moment(date.value).format('DD MMM YYYY'));

const text = computed(() => `${dateFormatted.value} ${time.value}`);

const onChange = () => {
  if (isSameDay(date.value)) {
    const availableTime = Math.max(splitTime(time.value), +moment().format('HH') + 1);
    time.value = `${getTimeFormatted(availableTime)}:00`;
  }

  const hour = splitTime(time.value);
  let nextDate = moment(date.value).startOf('day').add(hour, 'hour');

  if (hour === 24) {
    nextDate = nextDate.subtract(1, 'seconds');
  }

  emit('input', nextDate);
};

const toggleMenu = () => {
  isMenuOpen.value = false;
};
</script>

<style lang="scss" scoped>
.ui-period-picker {
  width: 100%;
  margin: 6px 0;
}

.picker-select {
  width: 66px;
  margin-left: 4px;
  margin-right: 4px;
}
</style>

<style lang="scss">
@import '@/styles/ui/period-picker.scss';
</style>
